import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Send, User, Sparkles, RefreshCw, Brain, TrendingUp, DollarSign, FileText, Search, Zap, MessageSquare, PieChart, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const suggestedQuestions = [
  { text: "How much did I spend on travel this month?", icon: TrendingUp },
  { text: "What's my total revenue?", icon: DollarSign },
  { text: "Which vendor costs me the most?", icon: FileText },
  { text: "Show me my expense breakdown by category", icon: PieChart },
  { text: "What invoices are still pending?", icon: FileText },
  { text: "Give me a financial health summary", icon: Brain }
];

const AIAssistant = () => {
  const [activeTab, setActiveTab] = useState('query');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [dataContext, setDataContext] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Natural Language Query - connects to /ai/query endpoint
  const handleNLQuery = async (query = input) => {
    if (!query.trim()) return;

    setLoading(true);
    setQueryResult(null);

    try {
      const response = await axios.post(`${API}/ai/query`, {
        query,
        session_id: sessionId
      });

      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      setQueryResult({
        query,
        answer: response.data.answer,
        timestamp: new Date().toLocaleTimeString()
      });
      setDataContext(response.data.data_context);
      setInput('');
    } catch (error) {
      toast.error('Failed to process query');
    } finally {
      setLoading(false);
    }
  };

  // Regular Chat - connects to /ai/chat endpoint
  const handleChat = async (message = input) => {
    if (!message.trim()) return;

    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message,
        session_id: sessionId
      });

      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get AI response');
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeTab === 'query') {
        handleNLQuery();
      } else {
        handleChat();
      }
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setQueryResult(null);
    setDataContext(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            AI Assistant
          </h1>
          <p className="text-muted-foreground mt-1">Powered by GPT-5.1 - Ask anything about your finances</p>
        </div>
        <Button 
          variant="outline" 
          className="border-orbit-border"
          onClick={handleNewChat}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          New Session
        </Button>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-orbit-card border border-orbit-border p-1">
          <TabsTrigger value="query" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <Search className="w-4 h-4 mr-2" />
            Natural Language Query
          </TabsTrigger>
          <TabsTrigger value="chat" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
            <MessageSquare className="w-4 h-4 mr-2" />
            AI Chat
          </TabsTrigger>
        </TabsList>

        {/* Natural Language Query Tab */}
        <TabsContent value="query" className="space-y-6">
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Query Input & Results */}
            <div className="lg:col-span-8 space-y-6">
              {/* Query Input Card */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-primary/5 border-purple-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Ask Your Financial Data</h3>
                      <p className="text-sm text-muted-foreground">Query your database using natural language</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="e.g., How much did I spend on travel last quarter?"
                      className="flex-1 bg-orbit-bg/50 border-purple-500/30 focus:border-purple-500"
                      disabled={loading}
                    />
                    <Button 
                      onClick={() => handleNLQuery()}
                      disabled={loading || !input.trim()}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Query Result */}
              <AnimatePresence>
                {queryResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card className="bg-orbit-card border-orbit-border">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Brain className="w-5 h-5 text-purple-400" />
                            <span className="text-white font-medium">AI Response</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{queryResult.timestamp}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Query: "{queryResult.query}"
                        </p>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-orbit-bg/50 rounded-xl p-4">
                          <p className="text-white whitespace-pre-wrap leading-relaxed">{queryResult.answer}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Data Context Summary */}
              {dataContext && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="bg-orbit-card border-orbit-border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-muted-foreground">Data Context Used</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="text-center p-3 rounded-lg bg-emerald-500/10">
                          <p className="text-xs text-muted-foreground">Revenue</p>
                          <p className="text-lg font-bold text-emerald-400">{formatCurrency(dataContext.total_revenue)}</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-red-500/10">
                          <p className="text-xs text-muted-foreground">Expenses</p>
                          <p className="text-lg font-bold text-red-400">{formatCurrency(dataContext.total_expenses)}</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-primary/10">
                          <p className="text-xs text-muted-foreground">Net Profit</p>
                          <p className={`text-lg font-bold ${dataContext.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(dataContext.net_profit)}
                          </p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-cyan-500/10">
                          <p className="text-xs text-muted-foreground">Bank Balance</p>
                          <p className="text-lg font-bold text-cyan-400">{formatCurrency(dataContext.bank_balance)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Empty State */}
              {!queryResult && !loading && (
                <Card className="bg-orbit-card border-orbit-border">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-purple-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">Query Your Financial Data</h3>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                      Ask questions in plain English like "How much did I spend on travel?" 
                      and get instant answers powered by AI.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Suggested Queries Sidebar */}
            <div className="lg:col-span-4">
              <Card className="bg-orbit-card border-orbit-border">
                <CardHeader>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Try These Queries
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {suggestedQuestions.map((item, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      className="w-full justify-start text-left h-auto py-3 px-4 text-sm text-muted-foreground hover:text-white hover:bg-purple-500/10"
                      onClick={() => handleNLQuery(item.text)}
                      disabled={loading}
                    >
                      <item.icon className="w-4 h-4 mr-3 text-purple-400" />
                      {item.text}
                    </Button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* AI Chat Tab */}
        <TabsContent value="chat">
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Chat Area */}
            <div className="lg:col-span-8">
              <Card className="bg-orbit-card border-orbit-border h-[600px] flex flex-col">
                <CardHeader className="border-b border-orbit-border">
                  <CardTitle className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 rounded-xl bg-orbit-glow flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    MyFinance AI Chat
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 rounded-2xl bg-orbit-glow/20 flex items-center justify-center mb-4">
                        <Sparkles className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">How can I help you today?</h3>
                      <p className="text-muted-foreground text-sm max-w-md">
                        I can help you understand your finances, explain reports, provide insights, 
                        and answer questions about Malaysia tax compliance.
                      </p>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                      >
                        {message.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div className={`max-w-[80%] p-4 rounded-2xl ${
                          message.role === 'user' 
                            ? 'bg-primary text-white rounded-br-md' 
                            : 'bg-orbit-bg/70 text-white rounded-bl-md'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        {message.role === 'user' && (
                          <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-secondary" />
                          </div>
                        )}
                      </motion.div>
                    ))
                  )}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-orbit-bg/70 p-4 rounded-2xl rounded-bl-md">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>
                <div className="p-4 border-t border-orbit-border">
                  <div className="flex gap-3">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about your finances..."
                      className="flex-1"
                      disabled={loading}
                      data-testid="ai-chat-input"
                    />
                    <Button 
                      onClick={() => handleChat()}
                      disabled={loading || !input.trim()}
                      className="bg-orbit-glow hover:opacity-90"
                      data-testid="ai-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* Capabilities */}
              <Card className="bg-orbit-card border-orbit-border gradient-border">
                <CardHeader>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    AI Capabilities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Analyze your financial data and provide insights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Explain financial reports in simple terms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Answer Malaysia tax compliance questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Provide business improvement suggestions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Help with LHDN e-Invoice requirements</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAssistant;
